/**
 * Adressen im Fließtext erkennen — EINE Stelle für Anzeige und Stimme.
 * (26.09.2026, Nachbesserung zu pre.3352, Ticket 2679)
 *
 * Die Blase (frontend/lib/markdown.ts) macht daraus Links, die Sprechfassung
 * (tools/tts-render.ts) sagt statt der Adresse „hier ist der Link“. Vorher
 * hatte jede Seite ihr eigenes Muster, und nur die Blase kannte nackte
 * Domains — vorgelesen wurde „github.com/settings/billing“ Zeichen für
 * Zeichen.
 *
 * ## Warum nackte Domains so eng erkannt werden
 *
 * pre.3352 verlinkte jede bekannte Endung, auch groß geschrieben und ohne
 * Pfad. In der Datenbank standen danach 37 Antworten mit „Wort.Es“ — aus
 * „Spanisch.Es macht“ wurde https://Spanisch.Es. Ebenso „gut.Es ging“,
 * „Ende.De“, „Neustadt.At“, „Welt.It's“, „Xcode.app“, „setup.app“,
 * „command.com“, „ASP.NET“, „5.de“, „foto.jpg.at“ und der Windows-Pfad
 * „C:\sites\ssi.at\“. Darum gilt jetzt:
 *
 *   - Host und Endung NUR in Kleinbuchstaben, und ohne den /i-Schalter:
 *     Mit /iu passen „ſ“ (langes s) und „K“ (Kelvin-Zeichen) auf [a-z].
 *   - Eine nackte Domain braucht einen Pfad („/“) oder „www.“ davor.
 *     „github.com/settings/billing“ ja, „voice.vespera.at“ allein nein.
 *   - Kein Treffer direkt nach einem Backslash und in keinem Wort, das einen
 *     Backslash trägt (Windows-Pfade).
 *   - Port und Pfad gehören dazu („example.com:8080/pfad“); Satzzeichen, ein
 *     „)“ oder „]“ ohne Gegenstück am Ende nicht.
 *
 * Mit Schema („https://…“, auch „HTTPS://…“) ist jede Adresse eine Adresse;
 * das Schema wird fürs Ziel klein geschrieben.
 */

/** Endungen, die eine nackte Domain tragen darf — nur klein geschrieben. */
const ENDUNGEN = "com|org|net|at|de|ch|eu|io|dev|app|info|co|uk|us|ai|fr|it|es|nl|gov|edu|me|be|gl|ly|tv";
const HOST = String.raw`(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:${ENDUNGEN})`;
const PORT = String.raw`(?::\d{1,5})?`;
/**
 * Was in einer Adresse stehen darf. Gedankenstrich, Auslassung und
 * typografische Anführung nie (in einer URL stünden sie kodiert), ein
 * Backslash auch nicht.
 */
const ZEICHEN = String.raw`[^\s<>"'—–…„“”‚‘’«»\\]`;
/**
 * Davor kein Buchstabe, keine Ziffer, kein @ (Mail-Adresse), Punkt, Strich,
 * Schrägstrich (Teil eines Pfads) und kein Backslash. \p{L}, weil \w keine
 * Umlaute kennt: „Häuser.at“ ist kein „user.at“.
 */
const DAVOR = String.raw`(?<![\p{L}\p{N}_@.\-/%\\])`;
/** Nach dem Host geht kein Wort weiter („ssi.atom“, „ssi.at.evil“). */
const HOST_ENDE = String.raw`(?![\p{L}\p{N}_@\\-]|\.[\p{L}\p{N}])`;

const MIT_SCHEMA = String.raw`[Hh][Tt][Tt][Pp][Ss]?:\/\/${ZEICHEN}+`;
const NACKT = String.raw`${DAVOR}(?:www\.${HOST}${PORT}${HOST_ENDE}(?:[/?#]${ZEICHEN}*)?|${HOST}${PORT}\/${ZEICHEN}*)`;

// ⚠️ Nur g und u — KEIN i (siehe oben).
const ADRESSE = new RegExp(`${MIT_SCHEMA}|${NACKT}`, "gu");
const SCHEMA = /^[Hh][Tt][Tt][Pp][Ss]?:\/\//;
/** Eine ganze Zeichenkette als nackte Domain (Markdown-Ziel, Prüfung nach dem Abtrennen). */
const NUR_NACKT = new RegExp(`^(?:${NACKT})$`, "u");

interface AdressTreffer {
  /** Beginn im Text. */
  start: number;
  /** Die Adresse, wie sie dastand — ohne Satzzeichen danach. */
  text: string;
  /** Das Ziel des Verweises: Schema klein, nackte Domain mit https://. */
  ziel: string;
}

/** Satzzeichen und Klammern ohne Gegenstück am Ende gehören zum Satz. */
function ohneNachlauf(url: string): string {
  let u = url;
  for (;;) {
    const letztes = u.slice(-1);
    if (/[.,;:!?]/.test(letztes)) {
      u = u.slice(0, -1);
      continue;
    }
    const auf = letztes === ")" ? "(" : letztes === "]" ? "[" : "";
    if (auf && u.split(auf).length < u.split(letztes).length) {
      u = u.slice(0, -1);
      continue;
    }
    return u;
  }
}

/** Liegt der Treffer in einem Wort mit Backslash — einem Windows-Pfad? */
function inWindowsPfad(text: string, start: number, laenge: number): boolean {
  const anfang = text.slice(0, start).search(/\S*$/);
  const rest = /^\S*/.exec(text.slice(start + laenge))?.[0] ?? "";
  return text.slice(anfang, start + laenge + rest.length).includes("\\");
}

/** Das Ziel einer erkannten Adresse: Schema klein, sonst https:// davor. */
function zielVon(adresse: string): string {
  const schema = SCHEMA.exec(adresse)?.[0];
  return schema ? schema.toLowerCase() + adresse.slice(schema.length) : `https://${adresse}`;
}

/** Alle Adressen in einem Text, der Reihe nach. */
export function adressenImText(text: string): AdressTreffer[] {
  const treffer: AdressTreffer[] = [];
  for (const m of text.matchAll(ADRESSE)) {
    const start = m.index ?? 0;
    const roh = m[0];
    if (!SCHEMA.test(roh) && inWindowsPfad(text, start, roh.length)) continue;
    const adresse = ohneNachlauf(roh);
    if (!adresse || (!SCHEMA.test(adresse) && !NUR_NACKT.test(adresse))) continue;
    treffer.push({ start, text: adresse, ziel: zielVon(adresse) });
  }
  return treffer;
}

/**
 * Ziel eines Markdown-Links `[Text](adresse)`: mit Schema (Schema klein) oder
 * eine nackte Domain nach DERSELBEN Regel wie im Fließtext (https:// davor).
 * Alles andere: null.
 */
export function linkZiel(roh: string): string | null {
  const adresse = roh.trim();
  if (SCHEMA.test(adresse)) return zielVon(adresse);
  return NUR_NACKT.test(adresse) ? zielVon(adresse) : null;
}

/** Jede Adresse im Text durch `ersatz` ersetzen (für die Stimme). */
export function ersetzeAdressen(text: string, ersatz: string): string {
  let aus = "";
  let zuletzt = 0;
  for (const t of adressenImText(text)) {
    aus += text.slice(zuletzt, t.start) + ersatz;
    zuletzt = t.start + t.text.length;
  }
  return aus + text.slice(zuletzt);
}
